/**
 * Pure helpers for the docx→relational suggestion sync (see useDocxSuggestionSync).
 *
 * Split out from the hook so the mapping logic — which tracked-change type
 * becomes which `document_suggestions.kind`, and which OOXML `w:id`s a single
 * change occupies — is unit-testable WITHOUT the editor package, Apollo, or a
 * live ProseMirror state. The hook is a thin Apollo wrapper around these.
 */

import type { TrackedChangesResult } from '@propdocs-editor/react/hooks';

// `TrackedChangeEntry` isn't re-exported from the react barrel; derive it from
// the result type so we track the package's shape without a deep import.
export type TrackedChangeEntry = TrackedChangesResult['entries'][number];

export type SuggestionSyncDocumentType = 'Document' | 'DocumentTemplate';

/**
 * Map a tracked-change entry type to the `kind` the backend records. Deletions
 * (inline + every structural `*Deletion`/`*Deleted` variant) → 'deletion';
 * an inline replace → 'replacement'; everything else (insertions + structural
 * inserts + property changes) → 'insertion'.
 */
export function entryKind(type: TrackedChangeEntry['type']): string {
  if (type === 'deletion' || type.endsWith('Deletion') || type.endsWith('Deleted')) {
    return 'deletion';
  }
  if (type === 'replacement') return 'replacement';
  return 'insertion';
}

/**
 * Every distinct revisionId a change occupies. A coalesced sidebar card can
 * cover several OOXML `w:id`s (a replacement carries a delete id + an insert
 * id; a multi-row table insert coalesces one id per row); each must get its
 * OWN relational row so accepting/rejecting any site reconciles correctly.
 */
export function revisionIdsOf(entry: TrackedChangeEntry): number[] {
  const ids = new Set<number>();
  if (typeof entry.revisionId === 'number') ids.add(entry.revisionId);
  if (typeof entry.insertionRevisionId === 'number') ids.add(entry.insertionRevisionId);
  for (const id of entry.coalescedRevisionIds ?? []) {
    if (typeof id === 'number') ids.add(id);
  }
  return [...ids];
}

/**
 * The globally-unique `external_id` for one tracked change site. `revisionId`
 * (the OOXML `w:id`) is only unique WITHIN one document, so it must be
 * namespaced by document type + id. Deterministic: the same change always
 * derives the same id, so re-syncing never creates a duplicate row.
 */
export function suggestionExternalId(
  documentType: SuggestionSyncDocumentType,
  documentId: string,
  revisionId: number
): string {
  return `docx:${documentType}:${documentId}:${revisionId}`;
}

/**
 * Build the full `external_id → representative-entry` map for a document's live
 * tracked changes. One entry can yield several ids (see {@link revisionIdsOf});
 * every id maps back to the entry so the create call can label the row.
 */
export function externalIdMap(
  entries: readonly TrackedChangeEntry[],
  documentType: SuggestionSyncDocumentType,
  documentId: string
): Map<string, TrackedChangeEntry> {
  const byExternalId = new Map<string, TrackedChangeEntry>();
  for (const entry of entries) {
    for (const rid of revisionIdsOf(entry)) {
      byExternalId.set(suggestionExternalId(documentType, documentId, rid), entry);
    }
  }
  return byExternalId;
}
